import { Typography } from '@mui/material'
import React, { Fragment, ChangeEvent, memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { SkipStep, UploadEIN } from 'src/constants/actionTypes'
import EINIcon from 'src/assets/icons/bgc.svg'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { Formik } from 'formik'
import { EINFormSchema } from './EINForm.validator'
import { FormikFileInput } from '../shared/form/formik/FormikFileInput'
import { EINFormValues } from './EINForm.interface'
import { FormErrorMessage } from '../shared/form/FormErrorMessage'
import { BasicRadioGroup } from '../shared/form/BasicRadioGroup'
import { FormikTextField } from '../shared/form/formik/FormikTextField'
import { FormikSelect } from '../shared/form/formik/FormikSelect'
import { states } from 'src/constants/states'
import { usePersona } from 'src/hooks/usePersona'
import { Button } from '../shared/Button'
import { useStepFormStorage } from 'src/hooks/useStepFormStorage'
import { SecureWrapper } from '../layout/SecureWrapper'
import { StepLabels } from '../client/step-labels/StepLabels'

const defaultValues: EINFormValues = {
  file: undefined,
  ein: '',
  businessName: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
}

const showFormValues = [
  {
    label: 'Yes',
    value: 'yes',
  },
  {
    label: 'No',
    value: 'no',
  },
]

const EIN_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]
const ZIP_CODE_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/]

const EINFormComponent = () => {
  const dispatch = useDispatch()

  const currentStep = useSelector(getCurrentCollectDataStep)

  const { startPersona } = usePersona()

  const [showForm, setShowForm] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const { savedValues, onValuesChange, clearStorage } = useStepFormStorage<EINFormValues>({
    storageKey: 'step_ein_form',
    fileFields: ['file'],
  })

  const initialValues = savedValues ?? defaultValues

  const handleFormSubmit = async (values: EINFormValues) => {
    if (!currentStep?.id) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      if (!showForm) {
        await createAsyncAction(dispatch, SkipStep.request({ stepId: currentStep.id }))
      } else {
        await createAsyncAction(
          dispatch,
          UploadEIN.request({
            ...values,
            stepId: currentStep.id,
            isBusinessEntityFlow: true,
          }),
        )

        clearStorage()

        await startPersona(currentStep.id)
      }
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeShowForm = useCallback((event: ChangeEvent<{ value: string }>) => {
    setShowForm(event.target.value === 'yes')
  }, [])

  useEffect(() => {
    if (!currentStep?.optional) {
      setShowForm(true)
    }
  }, [currentStep?.optional])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={showForm ? EINFormSchema : undefined}
    >
      {({ handleSubmit, values }) => {
        onValuesChange(values)

        return (
          <SecureWrapper
            footer={
              <Fragment>
                <FormErrorMessage css={tw`mb-4`} error={error} />
                <Button
                  className={tw`w-full`}
                  onClick={() => {
                    handleSubmit()
                  }}
                  loading={isLoading}
                  disabled={typeof showForm !== 'boolean'}
                  data-testid="contractors-onboarding-ein-submit-btn"
                >
                  Submit document
                </Button>
              </Fragment>
            }
          >
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
            >
              <img src={EINIcon} />
            </div>
            <StepLabels />
            <Typography css={tw`mb-6 mt-2`} variant="h2">
              Business Entity
            </Typography>
            <Typography css={tw`text-shades-400`}>
              Please enter your business information, if applicable.
            </Typography>
            {currentStep?.optional && (
              <div css={tw`mt-6 bg-shades-0 p-4 rounded-2xl mb-8`}>
                <Typography css={tw`mb-3 text-shades-400`} variant="h6">
                  Are you registering as a business entity? (This only applies to contractors who
                  have a registered business name and EIN, and wish to register with GigSafe as a
                  business. If this does not apply to you, please check 'No').
                </Typography>
                <BasicRadioGroup
                  options={showFormValues}
                  onChange={handleChangeShowForm}
                  dataTestId="contractors-onboarding-ein-business-option-input"
                />
              </div>
            )}
            {showForm && (
              <div>
                <Typography variant="overline" css={tw`text-shades-400`}>
                  ENTER YOUR BUSINESS INFORMATION
                </Typography>
                <div css={tw`bg-shades-0 p-4 rounded-2xl mb-8 mt-2`}>
                  <FormikTextField
                    name="ein"
                    css={tw`w-full mb-6`}
                    label="EIN"
                    placeholder="Enter your EIN"
                    mask={EIN_MASK}
                    inputProps={{ 'data-testid': 'contractors-onboarding-ein-number-input' }}
                  />
                  <FormikTextField
                    name="businessName"
                    css={tw`w-full`}
                    label="Business Name"
                    placeholder="Enter your business name"
                    inputProps={{
                      'data-testid': 'contractors-onboarding-ein-business-name-input',
                    }}
                  />
                </div>
                <Typography variant="overline" css={tw`text-shades-400`}>
                  ENTER YOUR BUSINESS ADDRESS
                </Typography>
                <div css={tw`bg-shades-0 p-4 rounded-2xl mb-8 mt-2`}>
                  <FormikTextField
                    name="streetAddress"
                    css={tw`w-full mb-6`}
                    label="Street address"
                    placeholder="Enter street address"
                    inputProps={{ 'data-testid': 'contractors-onboarding-ein-street-input' }}
                  />
                  <FormikTextField
                    name="city"
                    css={tw`w-full mb-6`}
                    label="City"
                    placeholder="Enter city"
                    inputProps={{ 'data-testid': 'contractors-onboarding-ein-city-input' }}
                  />
                  <div css={tw`w-full mb-6`}>
                    <FormikSelect
                      name="state"
                      label="State"
                      placeholder="Select state"
                      options={states}
                      inputProps={{ 'data-testid': 'contractors-onboarding-ein-state-input' }}
                    />
                  </div>
                  <FormikTextField
                    name="zipCode"
                    css={tw`w-full`}
                    label="Zip code"
                    placeholder="Enter zip code"
                    mask={ZIP_CODE_MASK}
                    inputProps={{ 'data-testid': 'contractors-onboarding-ein-zip-input' }}
                  />
                </div>
                <Typography variant="overline" css={tw`text-shades-400`}>
                  UPLOAD EIN LETTER
                </Typography>
                <div css={tw`bg-shades-0 p-4 rounded-2xl mb-8 mt-2`}>
                  <FormikFileInput
                    name="file"
                    inputDataTestId="contractors-onboarding-ein-letter-file-input"
                  />
                </div>
              </div>
            )}
          </SecureWrapper>
        )
      }}
    </Formik>
  )
}

export const EINForm = memo(EINFormComponent)
